import { useEffect, useState } from "react";
import { Activity, BarChart3, CalendarDays, Flame, LineChart, Newspaper } from "lucide-react";
import { TerminalShell } from "@/components/TerminalShell";
import { TradingViewEmbed } from "@/components/TradingViewEmbed";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";

const TOOLS = [
  {
    key: "symbols",
    label: "Mercados mundiais",
    icon: LineChart,
    script: "embed-widget-symbol-overview.js",
    config: {
      lineWidth: 2,
      lineType: 0,
      chartType: "area",
      fontColor: "rgb(106, 109, 120)",
      gridLineColor: "rgba(46, 46, 46, 0.06)",
      volumeUpColor: "rgba(34, 171, 148, 0.5)",
      volumeDownColor: "rgba(247, 82, 95, 0.5)",
      widgetFontColor: "#0F0F0F",
      upColor: "#22ab94",
      downColor: "#f7525f",
      borderUpColor: "#22ab94",
      borderDownColor: "#f7525f",
      wickUpColor: "#22ab94",
      wickDownColor: "#f7525f",
      locale: "br",
      chartOnly: false,
      scalePosition: "right",
      scaleMode: "Normal",
      fontFamily: "-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif",
      valuesTracking: "1",
      changeMode: "price-and-percent",
      symbols: [
        ["Apple", "NASDAQ:AAPL|1D"],
        ["Google", "NASDAQ:GOOGL|1D"],
        ["Microsoft", "NASDAQ:MSFT|1D"],
        ["NASDAQ:TSLA|1D"],
        ["NASDAQ:AMZN|1D"],
        ["NASDAQ:NFLX|1D"],
        ["BITSTAMP:BTCUSD|1D"],
        ["COINBASE:ETHUSD|1D"],
        ["BINANCE:BTCBRL|1D"],
        ["GEMINI:ELONUSD|1D"],
        ["COINBASE:TRUMPUSD|1D"],
        ["BINANCE:SOLUSDT|1D"],
        ["VANTAGE:SP500|1D"],
        ["FPMARKETS:EURO50|1D"],
        ["BMFBOVESPA:IBOV|1D"],
      ],
      dateRanges: ["1d|1", "1m|30", "3m|60", "12m|1D", "60m|1W", "all|1M"],
      fontSize: "10",
      headerFontSize: "medium",
      noTimeScale: false,
      hideDateRanges: false,
      hideMarketStatus: false,
      hideSymbolLogo: false,
    },
  },
  {
    key: "overview",
    label: "Visão de mercado",
    icon: Activity,
    script: "embed-widget-market-overview.js",
    config: {
      locale: "br",
      showFloatingTooltip: true,
      tabs: [
        { title: "Cripto", symbols: [{ s: "BINANCE:BTCUSDT" }, { s: "BINANCE:ETHUSDT" }, { s: "BINANCE:SOLUSDT" }] },
        { title: "Forex", symbols: [{ s: "FX:EURUSD" }, { s: "FX:GBPUSD" }, { s: "FX:USDJPY" }] },
        { title: "Commodities", symbols: [{ s: "TVC:GOLD" }, { s: "TVC:UKOIL" }, { s: "NYMEX:NG1!" }] },
        { title: "Ações", symbols: [{ s: "NASDAQ:AAPL" }, { s: "NASDAQ:TSLA" }, { s: "NASDAQ:NVDA" }] },
      ],
    },
  },
  {
    key: "screener",
    label: "Rastreador",
    icon: BarChart3,
    script: "embed-widget-screener.js",
    config: { defaultColumn: "overview", market: "crypto", showToolbar: true, locale: "br" },
  },
  {
    key: "heatmap",
    label: "Mapa de calor",
    icon: Flame,
    script: "embed-widget-crypto-coins-heatmap.js",
    config: { dataSource: "Crypto", blockSize: "market_cap_calc", blockColor: "change", locale: "br",
              hasTopBar: true, isDataSetEnabled: false, isZoomEnabled: true, hasSymbolTooltip: true },
  },
  {
    key: "calendar",
    label: "Calendário económico",
    icon: CalendarDays,
    script: "embed-widget-events.js",
    config: {
      locale: "br",
      countryFilter: "ar,au,br,ca,cn,fr,de,in,id,it,jp,kr,mx,ru,sa,za,tr,gb,us,eu",
      importanceFilter: "0,1",
    },
  },
  {
    key: "news",
    label: "Notícias do mercado",
    icon: Newspaper,
    script: "embed-widget-timeline.js",
    config: { displayMode: "adaptive", feedMode: "all_symbols", locale: "br" },
  },
];

export default function HelpPage() {
  const { t: tr } = useLanguage();
  const { user } = useAuth();
  const [tool, setTool] = useState(TOOLS[0]);
  const [height, setHeight] = useState(620);

  useEffect(() => {
    const onResize = () => setHeight(window.innerWidth < 768 ? 600 : 700);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <TerminalShell account={user?.name}>
      <div data-testid="analysis-tools-page" className="space-y-5 p-4 sm:p-6">
        <div>
          <h1 className="font-heading text-2xl font-light tracking-tight text-white">{tr("tools.title")}</h1>
          <p className="mt-1 text-sm text-[#8A97AD]">{tr("tools.subtitle")}</p>
        </div>

        <div className="grid grid-cols-2 gap-2 rounded-md border border-[#141A26] bg-[#0A0E16] p-1 sm:flex">
          {TOOLS.map((t) => (
            <button
              key={t.key}
              data-testid={`tool-tab-${t.key}`}
              onClick={() => setTool(t)}
              className={`flex flex-1 items-center justify-center gap-2 rounded px-3 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${
                tool.key === t.key ? "bg-[#132038] text-[#5B9CFF]" : "text-[#8A97AD] hover:text-white"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>

        <TradingViewEmbed
          key={tool.key}
          testId={`tool-widget-${tool.key}`}
          script={tool.script}
          config={tool.config}
          height={height}
          copyright="Dados por TradingView"
        />
      </div>
    </TerminalShell>
  );
}
